// src/pages/Onboarding/slides/AestheticGoalsSlide.js
import React from 'react';
import SlideShell from '../../SlideShell';
import LikeDislikeButton from '../../../../components/Onboarding/LikeDislikeButton';

// Simple fade + slide keyframes (scoped)
const FadeStyles = () => (
  <style>{`
    @keyframes fadeIn {
      from { opacity: 0; transform: scale(0.985) }
      to   { opacity: 1; transform: scale(1) }
    }
    .fade-in { animation: fadeIn 280ms ease-out both; }
    @media (prefers-reduced-motion: reduce) { .fade-in { animation: none; } }

    /* --- Slide animations for image transition --- */
    @keyframes outRight {
      from { transform: translateX(0%); opacity: 1; }
      to   { transform: translateX(100%); opacity: 0.8; }
    }
    @keyframes inFromLeft {
      from { transform: translateX(-100%); opacity: 0.8; }
      to   { transform: translateX(0%); opacity: 1; }
    }
    @keyframes outLeft {
      from { transform: translateX(0%); opacity: 1; }
      to   { transform: translateX(-100%); opacity: 0.8; }
    }
    @keyframes inFromRight {
      from { transform: translateX(100%); opacity: 0.8; }
      to   { transform: translateX(0%); opacity: 1; }
    }

    .img-anim {
      will-change: transform, opacity;
      backface-visibility: hidden;
      -webkit-backface-visibility: hidden;
      transform: translateZ(0);
    }

  /* Discard animations (duration is configurable via CSS var) */
  .out-right { animation: outRight var(--discard-anim-ms, 520ms) cubic-bezier(0.22, 1, 0.36, 1) both; }
  .in-left   { animation: inFromLeft var(--discard-anim-ms, 520ms) cubic-bezier(0.22, 1, 0.36, 1) both; }
  .out-left  { animation: outLeft var(--discard-anim-ms, 520ms) cubic-bezier(0.22, 1, 0.36, 1) both; }
  .in-right  { animation: inFromRight var(--discard-anim-ms, 520ms) cubic-bezier(0.22, 1, 0.36, 1) both; }

    @media (prefers-reduced-motion: reduce) {
      .out-right, .in-left, .out-left, .in-right { animation-duration: 1ms; }
    }
  `}</style>
);

export default function AestheticGoalsSlide({
  title,
  subtitle,
  currentStep,
  totalSteps,
  onNext,
  onBack,
  animationDirection = 'forward',
  isTransitioning = false,
  theme = 'dark',
  images = [],
  imagesFolder = 'W', // Expected values: W, B, A1, A4, O
  initialLikes = [],
  initialDislikes = [],
  onAutoSave = null,
  // NEW: configurable discard animation speed in ms
  discardAnimationMs = 700,
}) {
  const isDark = theme === 'dark';
  const pageBg = isDark ? 'bg-mm_black' : 'bg-white';

  const MAX_COUNT = 7;
  // Treat empty arrays or undefined as "no explicit images" to keep dependencies stable
  const explicitImages = Array.isArray(images) && images.length > 0 ? images : null;
  const sequence = React.useMemo(() => {
    // If explicit images passed, prefer them
    if (explicitImages) return explicitImages.slice(0, MAX_COUNT);
    const folder = String(imagesFolder || 'W').toUpperCase();
    const out = [];
    let ctx = null;
    try { ctx = require.context('../../../../images/AestheticGoals', true, /\.webp$/); } catch {}
    for (let i = 1; i <= MAX_COUNT; i += 1) {
      const rel = `./${folder}/${folder}${i}.webp`;
      try {
        const src = ctx ? ctx(rel) : rel;
        out.push(src);
      } catch {}
    }
    return out;
  }, [explicitImages, imagesFolder]);
  const [index, setIndex] = React.useState(0);
  const [likes, setLikes] = React.useState(() => (Array.isArray(initialLikes) ? [...initialLikes] : []));
  const [dislikes, setDislikes] = React.useState(() => (Array.isArray(initialDislikes) ? [...initialDislikes] : []));
  const autoSaveRef = React.useRef(null);

  React.useEffect(() => {
    setLikes(Array.isArray(initialLikes) ? [...initialLikes] : []);
    setDislikes(Array.isArray(initialDislikes) ? [...initialDislikes] : []);
    autoSaveRef.current = JSON.stringify({
      aestheticPreferenceLikes: Array.isArray(initialLikes) ? initialLikes : [],
      aestheticPreferenceDislikes: Array.isArray(initialDislikes) ? initialDislikes : [],
    });
  }, [initialLikes, initialDislikes]);

  const total = sequence.length;
  const isLast = index >= total - 1;
  const currentSrc = sequence[index];

  // Preload images on first mount to avoid late loads and mask flicker
  const [preloaded, setPreloaded] = React.useState(() => new Set());
  const [firstReady, setFirstReady] = React.useState(false);

  React.useEffect(() => {
    if (!sequence || sequence.length === 0) return;
    console.log('[AestheticGoalsSlide pre-load test] starting preload for folder sequence:', sequence);
    let canceled = false;
    const loaders = sequence.map((src, i) => new Promise((resolve) => {
      const img = new Image();
      img.decoding = 'sync';
      img.loading = 'eager';
      img.onload = () => {
        if (canceled) return;
        setPreloaded((prev) => {
          const next = new Set(prev);
          next.add(src);
          return next;
        });
        if (i === 0) {
          setFirstReady(true);
          console.log('[AestheticGoalsSlide pre-load test] first image loaded:', src);
        }
        resolve();
      };
      img.onerror = () => {
        if (canceled) return;
        console.warn('[AestheticGoalsSlide pre-load test] failed to preload:', src);
        resolve();
      };
      img.src = src;
    }));
    Promise.all(loaders).then(() => {
      if (canceled) return;
      console.log('[AestheticGoalsSlide pre-load test] preload complete');
    });
    return () => { canceled = true; };
  }, [sequence]);

  // Queue of outgoing animated cards to allow overlapping animations
  const [outgoing, setOutgoing] = React.useState([]); // { id, src, direction }
  const idRef = React.useRef(0);
  const [completed, setCompleted] = React.useState(false);

  const [contentKey, setContentKey] = React.useState(0);
  const [showContent, setShowContent] = React.useState(false);
  React.useEffect(() => {
    if (isTransitioning) {
      setShowContent(false);
      return;
    }
    let canceled = false;
    const waitUntil = () => {
      if (canceled) return;
      if (firstReady || !sequence || sequence.length === 0) {
        setContentKey((k) => k + 1);
        setShowContent(true);
        console.log('[AestheticGoalsSlide pre-load test] showing content; firstReady=', firstReady);
      } else {
        // small poll loop to avoid long timeouts
        setTimeout(waitUntil, 50);
      }
    };
    const id = setTimeout(waitUntil, 200);
    // Absolute fallback: ensure content shows after 1200ms even if firstReady didn't flip
    const fallbackId = setTimeout(() => {
      if (!canceled) {
        setShowContent(true);
        console.warn('[AestheticGoalsSlide pre-load test] fallback showing content after timeout');
      }
    }, 1200);
    return () => { canceled = true; clearTimeout(id); clearTimeout(fallbackId); };
  }, [isTransitioning, firstReady, sequence]);

  const commitAutoSave = React.useCallback((nextLikes, nextDislikes) => {
    if (typeof onAutoSave !== 'function') return;
    const payload = {
      aestheticPreferenceLikes: nextLikes,
      aestheticPreferenceDislikes: nextDislikes,
    };
    const serialized = JSON.stringify(payload);
    if (autoSaveRef.current === serialized) return;
    autoSaveRef.current = serialized;
    onAutoSave(payload);
  }, [onAutoSave]);

  React.useEffect(() => {
    commitAutoSave(likes, dislikes);
  }, [likes, dislikes, commitAutoSave]);

  const processAction = React.useCallback((type) => {
    if (index >= total) return; // deck exhausted
    const src = sequence[index];
    const direction = type === 'like' ? 'right' : 'left';
    const id = ++idRef.current;

    // Schedule this top card to animate out immediately
    setOutgoing((prev) => [...prev, { id, src, direction }]);

    // Mutually exclusive, de-duplicated preference bookkeeping
    if (type === 'like' && src) {
      setDislikes((prev) => prev.filter((s) => s !== src));
      setLikes((prev) => (prev.includes(src) ? prev : [...prev, src]));
    } else if (type === 'dislike' && src) {
      setLikes((prev) => prev.filter((s) => s !== src));
      setDislikes((prev) => (prev.includes(src) ? prev : [...prev, src]));
    }

    // Advance deck immediately so next press targets the next card
    setIndex((i) => i + 1);
  }, [index, total, sequence]);

  // Cleanup each outgoing card after its animation ends
  const handleOutgoingEnd = React.useCallback((id) => {
    setOutgoing((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // When deck is exhausted, finalize once
  React.useEffect(() => {
    if (!completed && index >= total) {
      onNext?.({ aestheticPreferenceLikes: likes, aestheticPreferenceDislikes: dislikes });
      setCompleted(true);
    }
  }, [index, total, completed, likes, dislikes, onNext]);

  const footer = (
    <div className="flex items-center justify-between gap-3">
      <LikeDislikeButton
        variant="dislike"
        theme={theme}
        className="w-1/2"
        onClick={() => processAction('dislike')}
        disabled={index >= total}
      >
        Dislike
      </LikeDislikeButton>

      <LikeDislikeButton
        variant="like"
        theme={theme}
        className="w-1/2"
        onClick={() => processAction('like')}
        disabled={index >= total}
      >
        Like
      </LikeDislikeButton>
    </div>
  );

  return (
    <div className={`min-h-screen ${pageBg}`} style={{ ['--discard-anim-ms']: `${discardAnimationMs}ms` }}>
      <SlideShell
        title={title}
        subtitle={subtitle}
        currentStep={currentStep}
        totalSteps={totalSteps}
        onNext={() => onNext?.({ aestheticPreferenceLikes: likes, aestheticPreferenceDislikes: dislikes })}
        onBack={onBack}
        footer={footer}
        theme={theme}
        animationDirection={animationDirection}
        isTransitioning={isTransitioning}

      >
        <FadeStyles />
        {showContent && (
          <div key={contentKey} className="fade-in">
            <div className="relative w-full rounded-2xl overflow-hidden" style={{ height: 340 }}>
              {/* Current top card (static) */}
              {index < total && currentSrc && (
                <img
                  src={currentSrc}
                  alt={`Style ${index + 1}`}
                  className="absolute inset-0 w-full h-full object-contain"
                  loading="eager"
                  decoding="sync"
                  onLoad={() => console.log('[AestheticGoalsSlide pre-load test] current image displayed:', currentSrc)}
                />
              )}

              {/* Any number of outgoing cards animating simultaneously above the stack */}
              {outgoing.map((item, i) => {
                const isLike = item.direction === 'right';
                const animClass = isLike ? 'out-right' : 'out-left';
                return (
                  <div
                    key={item.id}
                    onAnimationEnd={(e) => {
                      if (e.currentTarget !== e.target) return;
                      handleOutgoingEnd(item.id);
                    }}
                    className={`img-anim absolute inset-0 ${animClass}`}
                    style={{ zIndex: 10 + i }}
                    aria-hidden
                  >
                    {/* Image */}
                    <img
                      src={item.src}
                      alt=""
                      className="absolute inset-0 w-full h-full object-contain"
                      loading="eager"
                      decoding="sync"
                    />

                    {/* Tint overlay masked to the image bounds */}
                    <div
                      className="absolute inset-0"
                      style={{
                        backgroundColor: isLike ? 'rgba(16,185,129,0.35)' : 'rgba(239,68,68,0.35)',
                        WebkitMaskImage: `url(${item.src})`,
                        maskImage: `url(${item.src})`,
                        WebkitMaskSize: 'contain',
                        maskSize: 'contain',
                        WebkitMaskRepeat: 'no-repeat',
                        maskRepeat: 'no-repeat',
                        WebkitMaskPosition: 'center',
                        maskPosition: 'center',
                      }}
                    />

                    {/* Center icon */}
                    <div className="absolute inset-0 flex items-center justify-center p-6">
                      {isLike ? (
                        <svg
                          viewBox="0 0 100 100"
                          width="70%"
                          height="70%"
                          role="img"
                          aria-label="Liked"
                        >
                          <path
                            d="M20 52 L42 74 L80 28"
                            fill="none"
                            stroke="#ffffff"
                            strokeWidth="12"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        <svg
                          viewBox="0 0 100 100"
                          width="70%"
                          height="70%"
                          role="img"
                          aria-label="Disliked"
                        >
                          <path
                            d="M25 25 L75 75"
                            fill="none"
                            stroke="#ffffff"
                            strokeWidth="12"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M75 25 L25 75"
                            fill="none"
                            stroke="#ffffff"
                            strokeWidth="12"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={`mt-3 text-sm text-center ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {Math.min(index + 1, total)} / {total}
            </div>
          </div>
        )}
      </SlideShell>
    </div>
  );
}
